import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link, useSearchParams } from "react-router-dom"
import { Box, ChevronRight, Factory, FolderOpen, Puzzle } from "lucide-react"
import { AmpelBadge } from "@/components/AmpelBadge"
import { Badge, Input } from "@/components/ui"
import { cn } from "@/lib/utils"
import {
  fetchStrukturTree,
  type Ampel,
  type StrukturAnlage,
  type StrukturKomponente,
  type StrukturObjekt,
} from "@/lib/api"
import { KomponenteDetailContent } from "./KomponenteDetailPage"
import { ObjektDetailContent } from "./ObjektDetailPage"

/**
 * Struktur-Explorer (docs/STRUKTUR_TREE_KONZEPT.md) — der gesamte Bestand
 * Anlage → Objekt (optional) → Komponente als aufklappbarer Baum, links der
 * Baum mit Suche, rechts das Detail des selektierten Knotens.
 *
 * Kernentscheidungen (Befund MASCHINEN_STRUKTURVERGLEICH.md):
 *  - 96 % der Komponenten hängen DIREKT an der Anlage — sie kommen unter einen
 *    standardmäßig zugeklappten Sammelknoten „Direkt zugeordnet (n)", damit
 *    der Baum nicht zur 5000-px-Flachliste degeneriert (Nitrierung: 88 Stück).
 *  - Suche filtert clientseitig über Bezeichnung/TAG-Nummer/Objekt-Code und
 *    zeigt Treffer mitsamt Vorfahren (Cmd-K-Anschluss: ?select=komponente-42).
 *  - Auswahl wandert als ?select=<typ>-<id> in die URL (Deep-Links, Zurück),
 *    der Aufklapp-Zustand überlebt in sessionStorage.
 *  - Ampel je Knoten kommt aggregiert (worst-of) aus GET /struktur/tree.
 */

type KnotenTyp = "anlage" | "objekt" | "komponente"

interface Auswahl {
  typ: KnotenTyp
  id: number
}

interface TreeRow {
  key: string
  typ: KnotenTyp | "gruppe"
  id: number
  tiefe: number
  label: string
  /** Sekundärtext (TAG-Unit/-Nummer bzw. Objekt-Code) */
  code: string | null
  ampel: Ampel | null
  anzahl: number | null
  hatKinder: boolean
  expanded: boolean
  gesperrt: boolean
  anlageId: number
  objektId: number | null
}

const EXPANSION_STORAGE_KEY = "technik-struktur-expanded"

function ladeExpansion(): Set<string> {
  try {
    const roh = sessionStorage.getItem(EXPANSION_STORAGE_KEY)
    return new Set(roh ? (JSON.parse(roh) as string[]) : [])
  } catch {
    return new Set()
  }
}

function parseAuswahl(param: string | null): Auswahl | null {
  const match = param?.match(/^(anlage|objekt|komponente)-(\d+)$/)
  if (!match) return null
  return { typ: match[1] as KnotenTyp, id: Number(match[2]) }
}

/** Vorfahren-Schlüssel eines Knotens (zum Aufklappen bei Deep-Link/Auswahl). */
function vorfahrenKeys(auswahl: Auswahl, anlagen: StrukturAnlage[]): string[] {
  if (auswahl.typ === "anlage") return []
  for (const a of anlagen) {
    if (auswahl.typ === "objekt" && a.objekte.some((o) => o.id === auswahl.id)) {
      return [`a${a.id}`]
    }
    if (auswahl.typ === "komponente") {
      const objekt = a.objekte.find((o) => o.komponenten.some((k) => k.id === auswahl.id))
      if (objekt) return [`a${a.id}`, `o${objekt.id}`]
      if (a.komponenten.some((k) => k.id === auswahl.id)) return [`a${a.id}`, `d${a.id}`]
    }
  }
  return []
}

function normalisiert(wert: string | null | undefined): string {
  return (wert ?? "").toLowerCase()
}

const AMPEL_PUNKT_FARBE: Record<Ampel, string> = {
  gruen: "bg-green-500",
  orange: "bg-orange-400",
  rot: "bg-red-500",
}

const AMPEL_LABEL: Record<Ampel, string> = { gruen: "grün", orange: "orange", rot: "rot" }

/** Kleiner Status-Punkt für Baumzeilen (die Badge wäre zu laut für 800+ Zeilen). */
function AmpelPunkt({ ampel }: { ampel: Ampel | null }) {
  if (!ampel) return null
  return (
    <span
      className={`h-2 w-2 shrink-0 rounded-full ${AMPEL_PUNKT_FARBE[ampel]}`}
      title={`Fristen-Ampel: ${AMPEL_LABEL[ampel]}`}
      aria-label={`Fristen-Ampel: ${AMPEL_LABEL[ampel]}`}
    />
  )
}

const KNOTEN_ICON: Record<TreeRow["typ"], typeof Factory> = {
  anlage: Factory,
  objekt: Box,
  komponente: Puzzle,
  gruppe: FolderOpen,
}

export function StrukturPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [suche, setSuche] = useState("")
  const [expansion, setExpansion] = useState<Set<string>>(ladeExpansion)
  const treeRef = useRef<HTMLDivElement>(null)
  const initialisiert = useRef(false)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["struktur-tree"],
    queryFn: fetchStrukturTree,
  })

  const auswahl = parseAuswahl(searchParams.get("select"))
  const auswahlKey = auswahl
    ? `${auswahl.typ === "anlage" ? "a" : auswahl.typ === "objekt" ? "o" : "k"}${auswahl.id}`
    : null

  // Aufklapp-Zustand persistieren (überlebt Navigation innerhalb der Session).
  useEffect(() => {
    sessionStorage.setItem(EXPANSION_STORAGE_KEY, JSON.stringify([...expansion]))
  }, [expansion])

  // Deep-Link (?select=…, z. B. aus Cmd-K): einmalig Vorfahren aufklappen
  // und den Knoten ins Bild scrollen, sobald die Daten da sind.
  useEffect(() => {
    if (!data || initialisiert.current) return
    initialisiert.current = true
    if (!auswahl) return
    const vorfahren = vorfahrenKeys(auswahl, data)
    if (vorfahren.length > 0) {
      setExpansion((prev) => new Set([...prev, ...vorfahren]))
    }
    requestAnimationFrame(() => {
      treeRef.current
        ?.querySelector(`[data-key="${auswahlKey}"]`)
        ?.scrollIntoView({ block: "center" })
    })
  }, [data, auswahl, auswahlKey])

  const sucheAktiv = suche.trim().length > 0

  /**
   * Sichtbare Baumzeilen. Zwei Modi:
   *  - ohne Suche: klassischer Baum, Sichtbarkeit über den Aufklapp-Zustand;
   *  - mit Suche: Treffer + ihre Vorfahren, Pfade automatisch aufgeklappt
   *    (der manuelle Aufklapp-Zustand bleibt unangetastet).
   */
  const zeilen = useMemo<TreeRow[]>(() => {
    if (!data) return []
    const q = normalisiert(suche.trim())
    const rows: TreeRow[] = []

    const matchKomponente = (k: StrukturKomponente) =>
      !q || normalisiert(k.beschreibung).includes(q) || normalisiert(k.tagNummer).includes(q)
    const matchObjekt = (o: StrukturObjekt) =>
      !q || normalisiert(o.bezeichnung).includes(q) || normalisiert(o.objektCode).includes(q)
    const matchAnlage = (a: StrukturAnlage) =>
      !q ||
      normalisiert(a.bezeichnung).includes(q) ||
      normalisiert(a.kostenstelleNavision).includes(q) ||
      (a.tagUnit !== null && String(a.tagUnit).padStart(3, "0").includes(q))

    const komponenteZeile = (k: StrukturKomponente, tiefe: number, a: StrukturAnlage, objektId: number | null) => {
      rows.push({
        key: `k${k.id}`,
        typ: "komponente",
        id: k.id,
        tiefe,
        label: k.beschreibung,
        code: k.tagNummer,
        ampel: k.ampel,
        anzahl: null,
        hatKinder: false,
        expanded: false,
        gesperrt: k.gesperrt,
        anlageId: a.id,
        objektId,
      })
    }

    for (const a of data) {
      const objektTreffer = a.objekte.map((o) => ({
        objekt: o,
        selbst: matchObjekt(o),
        kinder: o.komponenten.filter(matchKomponente),
      }))
      const direkteTreffer = a.komponenten.filter(matchKomponente)
      const anlageSelbst = matchAnlage(a)
      const hatTreffer =
        anlageSelbst ||
        objektTreffer.some((t) => t.selbst || t.kinder.length > 0) ||
        direkteTreffer.length > 0
      if (sucheAktiv && !hatTreffer) continue

      const anlageExpanded = sucheAktiv
        ? objektTreffer.some((t) => t.selbst || t.kinder.length > 0) || direkteTreffer.length > 0
        : expansion.has(`a${a.id}`)
      const komponentenGesamt =
        a.komponenten.length + a.objekte.reduce((n, o) => n + o.komponenten.length, 0)
      rows.push({
        key: `a${a.id}`,
        typ: "anlage",
        id: a.id,
        tiefe: 0,
        label: a.bezeichnung,
        code: a.tagUnit !== null ? String(a.tagUnit).padStart(3, "0") : null,
        ampel: a.ampel,
        anzahl: komponentenGesamt,
        hatKinder: a.objekte.length > 0 || a.komponenten.length > 0,
        expanded: anlageExpanded,
        gesperrt: false,
        anlageId: a.id,
        objektId: null,
      })
      if (!anlageExpanded) continue

      for (const { objekt: o, selbst, kinder } of objektTreffer) {
        if (sucheAktiv && !selbst && kinder.length === 0) continue
        const objektExpanded = sucheAktiv ? kinder.length > 0 : expansion.has(`o${o.id}`)
        rows.push({
          key: `o${o.id}`,
          typ: "objekt",
          id: o.id,
          tiefe: 1,
          label: o.bezeichnung,
          code: o.objektCode,
          ampel: o.ampel,
          anzahl: o.komponenten.length,
          hatKinder: o.komponenten.length > 0,
          expanded: objektExpanded,
          gesperrt: false,
          anlageId: a.id,
          objektId: o.id,
        })
        if (!objektExpanded) continue
        for (const k of sucheAktiv ? kinder : o.komponenten) komponenteZeile(k, 2, a, o.id)
      }

      if (a.komponenten.length > 0 && (!sucheAktiv || direkteTreffer.length > 0)) {
        const gruppeExpanded = sucheAktiv ? true : expansion.has(`d${a.id}`)
        rows.push({
          key: `d${a.id}`,
          typ: "gruppe",
          id: a.id,
          tiefe: 1,
          label: "Direkt zugeordnet",
          code: null,
          ampel: null,
          anzahl: a.komponenten.length,
          hatKinder: true,
          expanded: gruppeExpanded,
          gesperrt: false,
          anlageId: a.id,
          objektId: null,
        })
        if (gruppeExpanded) {
          for (const k of sucheAktiv ? direkteTreffer : a.komponenten) komponenteZeile(k, 2, a, null)
        }
      }
    }
    return rows
  }, [data, suche, sucheAktiv, expansion])

  function toggle(key: string) {
    setExpansion((prev) => {
      const neu = new Set(prev)
      if (neu.has(key)) neu.delete(key)
      else neu.add(key)
      return neu
    })
  }

  function auswaehlen(row: TreeRow) {
    if (row.typ === "gruppe") {
      if (!sucheAktiv) toggle(row.key)
      return
    }
    setSearchParams({ select: `${row.typ}-${row.id}` })
    if (sucheAktiv || !row.hatKinder) return
    // Bequemlichkeit: Auswahl klappt den Knoten auf (nie zu).
    setExpansion((prev) => new Set(prev).add(row.key))
  }

  function fokussiere(key: string | undefined) {
    if (!key) return
    treeRef.current?.querySelector<HTMLElement>(`[data-key="${key}"]`)?.focus()
  }

  /** Baum-Tastatur (roving über data-key): ↑/↓ bewegen, →/← auf-/zuklappen. */
  function onTreeKeyDown(e: React.KeyboardEvent) {
    const key = (e.target as HTMLElement).dataset?.key
    if (!key) return
    const idx = zeilen.findIndex((r) => r.key === key)
    if (idx < 0) return
    const row = zeilen[idx]
    if (e.key === "ArrowDown") {
      e.preventDefault()
      fokussiere(zeilen[idx + 1]?.key)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      fokussiere(zeilen[idx - 1]?.key)
    } else if (e.key === "ArrowRight" && !sucheAktiv) {
      e.preventDefault()
      if (row.hatKinder && !row.expanded) toggle(row.key)
      else fokussiere(zeilen[idx + 1]?.key)
    } else if (e.key === "ArrowLeft" && !sucheAktiv) {
      e.preventDefault()
      if (row.expanded) toggle(row.key)
      else {
        // zum Eltern-Knoten (nächste Zeile darüber mit geringerer Tiefe)
        for (let i = idx - 1; i >= 0; i--) {
          if (zeilen[i].tiefe < row.tiefe) {
            fokussiere(zeilen[i].key)
            break
          }
        }
      }
    }
  }

  const auswahlAnlage =
    auswahl?.typ === "anlage" ? data?.find((a) => a.id === auswahl.id) : undefined

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Struktur</h1>
        <p className="mt-1 text-sm text-slate-600">
          Anlage → Objekt → Komponente als Baum — die Objekt-Ebene ist optional, die meisten
          Komponenten hängen direkt an der Anlage.
        </p>
      </header>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Struktur …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Struktur konnte nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {data && (
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(330px,420px)_minmax(0,1fr)]">
          {/* Baum */}
          <section className="rounded-lg border border-slate-200 bg-white lg:sticky lg:top-4">
            <div className="border-b border-slate-200 p-3">
              <Input
                type="text"
                placeholder="TAG-Nummer oder Bezeichnung…"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                aria-label="Struktur durchsuchen"
                className="w-full"
              />
            </div>
            <div
              ref={treeRef}
              role="tree"
              aria-label="Anlagenstruktur"
              onKeyDown={onTreeKeyDown}
              className="max-h-[calc(100vh-16rem)] overflow-y-auto p-2"
            >
              {zeilen.length === 0 && (
                <p className="px-3 py-6 text-center text-sm text-slate-500">
                  {sucheAktiv ? "Keine Treffer." : "Noch keine Anlagen erfasst."}
                </p>
              )}
              {zeilen.map((row) => {
                const Icon = KNOTEN_ICON[row.typ]
                const istAuswahl = row.key === auswahlKey
                return (
                  <div
                    key={row.key}
                    role="treeitem"
                    aria-expanded={row.hatKinder ? row.expanded : undefined}
                    aria-selected={istAuswahl}
                    className="flex items-center"
                    style={{ paddingLeft: `${row.tiefe * 1.25}rem` }}
                  >
                    {row.hatKinder && !sucheAktiv ? (
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => toggle(row.key)}
                        aria-label={`${row.label} ${row.expanded ? "zuklappen" : "aufklappen"}`}
                        className="rounded-md p-0.5 text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60"
                      >
                        <ChevronRight
                          className={cn("h-4 w-4 transition-transform", row.expanded && "rotate-90")}
                        />
                      </button>
                    ) : (
                      <span className="w-5 shrink-0" aria-hidden="true" />
                    )}
                    <button
                      type="button"
                      data-key={row.key}
                      onClick={() => auswaehlen(row)}
                      className={cn(
                        "flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60",
                        istAuswahl ? "bg-slate-100 font-medium text-slate-900" : "text-slate-700 hover:bg-slate-50",
                        row.typ === "gruppe" && "text-slate-500",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                      <span className={cn("truncate", row.gesperrt && "line-through decoration-slate-400")}>
                        {row.label}
                      </span>
                      {row.code && (
                        <span className="shrink-0 font-mono text-[11px] text-slate-400">{row.code}</span>
                      )}
                      {row.anzahl !== null && row.anzahl > 0 && (
                        <span className="shrink-0 rounded-full border border-slate-200 px-1.5 text-[11px] text-slate-500">
                          {row.anzahl}
                        </span>
                      )}
                      <span className="ml-auto flex shrink-0 items-center pl-1">
                        <AmpelPunkt ampel={row.ampel} />
                      </span>
                    </button>
                  </div>
                )
              })}
            </div>
          </section>

          {/* Detail-Panel */}
          <section className="min-w-0">
            {!auswahl && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
                Knoten im Baum auswählen, um Details zu sehen — Anlagen und Objekte lassen sich
                über die Pfeile aufklappen.
              </div>
            )}

            {auswahl?.typ === "anlage" && auswahlAnlage && (
              <div className="rounded-lg border border-slate-200 bg-white p-6">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{auswahlAnlage.bezeichnung}</h2>
                    <p className="mt-1 text-sm text-slate-600">
                      TAG-Unit{" "}
                      <span className="font-mono">
                        {auswahlAnlage.tagUnit !== null
                          ? String(auswahlAnlage.tagUnit).padStart(3, "0")
                          : "—"}
                      </span>
                      {auswahlAnlage.kostenstelleNavision
                        ? ` · Kostenstelle ${auswahlAnlage.kostenstelleNavision}`
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {auswahlAnlage.ampel && <AmpelBadge ampel={auswahlAnlage.ampel} />}
                    <Badge tone={auswahlAnlage.aktiv ? "green" : "slate"}>
                      {auswahlAnlage.aktiv ? "aktiv" : "inaktiv"}
                    </Badge>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
                  <div>
                    <dt className="text-slate-500">Objekte</dt>
                    <dd className="text-slate-900">{auswahlAnlage.objekte.length}</dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Komponenten gesamt</dt>
                    <dd className="text-slate-900">
                      {auswahlAnlage.komponenten.length +
                        auswahlAnlage.objekte.reduce((n, o) => n + o.komponenten.length, 0)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">davon direkt</dt>
                    <dd className="text-slate-900">{auswahlAnlage.komponenten.length}</dd>
                  </div>
                </dl>
                <Link
                  to={`/maschinen/anlagen/${auswahlAnlage.id}`}
                  className="mt-5 inline-flex h-9 items-center rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Vollansicht öffnen
                </Link>
              </div>
            )}

            {auswahl?.typ === "objekt" && (
              <div>
                <div className="mb-4 text-right">
                  <Link
                    to={`/maschinen/objekte/${auswahl.id}`}
                    className="text-sm text-slate-500 underline hover:text-slate-800"
                  >
                    Vollansicht öffnen
                  </Link>
                </div>
                <ObjektDetailContent objektId={auswahl.id} />
              </div>
            )}

            {auswahl?.typ === "komponente" && (
              <div>
                <div className="mb-4 text-right">
                  <Link
                    to={`/maschinen/komponenten/${auswahl.id}`}
                    className="text-sm text-slate-500 underline hover:text-slate-800"
                  >
                    Vollansicht öffnen
                  </Link>
                </div>
                <KomponenteDetailContent komponenteId={auswahl.id} />
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
